import BaseClient, { type ExtendedAxiosRequestConfig } from '../BaseClient';
import type { QueryParams } from '~/services/api';
import type {
    CreateWorkflow,
    CreateWorkflowComment,
    StopMode,
    UpdateWorkflow,
    UpdateWorkflowComment,
    WorkflowsClientInterface,
} from './workflowsClient.types';
import type { WorkflowDocumentInput } from '~/types/workflow.types';
import type {
    SimulationApiDestinationRequest,
    SimulationCriteriaRequest,
    SimulationSegmentsRequest,
    SimulationSendRequest,
} from '~/types/workflowSimulation.types';

export default class CorporateWorkflowsClient extends BaseClient implements WorkflowsClientInterface {
    constructor(private teamId: number) {
        super();
    }

    getWorkflows() {
        const url = `/api/v3/teams/${this.teamId}/workflows`;
        return this.makeCoreApiRequest(url);
    }

    getWorkflowUsage(params: QueryParams) {
        const url = `/api/v3/teams/${this.teamId}/workflows/usage`;
        return this.makeCoreApiRequest(url, 'get', null, params);
    }

    getWorkflow(workflowId: number, config?: ExtendedAxiosRequestConfig) {
        const url = `/api/v3/teams/${this.teamId}/workflows/${workflowId}`;
        return this.makeCoreApiRequest(url, 'get', null, null, config);
    }

    createWorkflow(input: CreateWorkflow) {
        const url = `/api/v3/teams/${this.teamId}/workflows`;
        return this.makeCoreApiRequest(url, 'post', input);
    }

    updateWorkflow(workflowId: number, input: UpdateWorkflow) {
        const url = `/api/v3/teams/${this.teamId}/workflows/${workflowId}`;
        return this.makeCoreApiRequest(url, 'put', input);
    }

    archiveWorkflow(workflowId: number, mode?: StopMode) {
        const url = `/api/v3/teams/${this.teamId}/workflows/${workflowId}/archive`;
        return this.makeCoreApiRequest(url, 'post', mode ? { mode } : {});
    }

    getWorkflowVersions(workflowId: number, config?: ExtendedAxiosRequestConfig) {
        const url = `/api/v3/teams/${this.teamId}/workflows/${workflowId}/versions`;
        return this.makeCoreApiRequest(url, 'get', null, null, config);
    }

    getWorkflowVersion(workflowId: number, version: number, config?: ExtendedAxiosRequestConfig) {
        const url = `/api/v3/teams/${this.teamId}/workflows/${workflowId}/versions/${version}`;
        return this.makeCoreApiRequest(url, 'get', null, null, config);
    }

    updateWorkflowVersion(workflowId: number, version: number, input: WorkflowDocumentInput) {
        const url = `/api/v3/teams/${this.teamId}/workflows/${workflowId}/versions/${version}`;
        return this.makeCoreApiRequest(url, 'put', input);
    }

    publishWorkflowVersion(workflowId: number, version: number, mode?: StopMode) {
        const url = `/api/v3/teams/${this.teamId}/workflows/${workflowId}/versions/${version}/publish`;
        return this.makeCoreApiRequest(url, 'post', mode ? { mode } : {});
    }

    terminateWorkflowVersion(workflowId: number, version: number) {
        const url = `/api/v3/teams/${this.teamId}/workflows/${workflowId}/versions/${version}/terminate`;
        return this.makeCoreApiRequest(url, 'post');
    }

    getWorkflowOccupancy(workflowId: number) {
        const url = `/api/v3/teams/${this.teamId}/workflows/${workflowId}/occupancy`;
        return this.makeCoreApiRequest(url);
    }

    evaluateWorkflowSimulationCriteria(input: SimulationCriteriaRequest, config?: ExtendedAxiosRequestConfig) {
        const url = `/api/v3/teams/${this.teamId}/workflow-simulation/criteria`;
        return this.makeCoreApiRequest(url, 'post', input, null, config);
    }

    evaluateWorkflowSimulationSegments(input: SimulationSegmentsRequest, config?: ExtendedAxiosRequestConfig) {
        const url = `/api/v3/teams/${this.teamId}/workflow-simulation/segments`;
        return this.makeCoreApiRequest(url, 'post', input, null, config);
    }

    previewWorkflowSimulationSend(input: SimulationSendRequest, config?: ExtendedAxiosRequestConfig) {
        const url = `/api/v3/teams/${this.teamId}/workflow-simulation/send`;
        return this.makeCoreApiRequest(url, 'post', input, null, config);
    }

    previewWorkflowSimulationApiDestination(
        input: SimulationApiDestinationRequest,
        config?: ExtendedAxiosRequestConfig
    ) {
        const url = `/api/v3/teams/${this.teamId}/workflow-simulation/api-destination`;
        return this.makeCoreApiRequest(url, 'post', input, null, config);
    }

    getWorkflowComments() {
        const url = `/api/v3/teams/${this.teamId}/workflow-comments`;
        return this.makeCoreApiRequest(url);
    }

    createWorkflowComment(input: CreateWorkflowComment) {
        const url = `/api/v3/teams/${this.teamId}/workflow-comments`;
        return this.makeCoreApiRequest(url, 'post', input);
    }

    updateWorkflowComment(commentId: number, input: UpdateWorkflowComment) {
        const url = `/api/v3/teams/${this.teamId}/workflow-comments/${commentId}`;
        return this.makeCoreApiRequest(url, 'put', input);
    }

    deleteWorkflowComment(commentId: number) {
        const url = `/api/v3/teams/${this.teamId}/workflow-comments/${commentId}`;
        return this.makeCoreApiRequest(url, 'delete');
    }
}
